/**
 * The two auth guards every protected route passes through.
 *
 * Extracted from App.tsx so the redirect they perform can be tested: both of
 * them throw the URL away as they redirect, which is a bug users hit and no
 * test could see (GET-13956).
 */
import {
  Navigate, useLocation,
} from 'react-router-dom'
import {
  useAuth,
} from '../hooks/useAuth'
import {
  loginPathForRoute,
} from '../utils/routes'
import {
  rememberPendingRoute,
} from '../utils/pendingRoute'

export function ProtectedRoute({ children }: { children: JSX.Element }): JSX.Element {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return <div className="min-h-screen" />
  }

  if (!isAuthenticated) {
    // <Navigate> carries no search string, and `loginPathForRoute` distils the
    // route down to a bare `?source=` slug, so whatever the link actually asked
    // for is gone by the time the page mounts (GET-13956). Remember the URL
    // itself; CallbackPage resumes it.
    rememberPendingRoute(location.pathname, location.search)
    return (
      <Navigate
        to={loginPathForRoute(location.pathname, location.search)}
        replace
      />
    )
  }

  return children
}

export function SetupGuard({ children }: { children: JSX.Element }): JSX.Element {
  const { user } = useAuth()
  const location = useLocation()

  if (user && user.setup_completed_at == null) {
    // The bounce that caught user 3574: signed in, but stopped at the wizard's
    // billing step, so every deep link we sent him became a trip to onboarding
    // with its query string thrown away (GET-13956). The wizard's exit resumes
    // what is remembered here.
    rememberPendingRoute(location.pathname, location.search)
    return <Navigate to="/onboarding" replace />
  }

  return children
}
